import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import type { TopicFact } from 'modules/studyPlan';

interface TopicListProps {
  testId: string;
  title: string;
  empty: string;
  topics: TopicFact[];
  tone: 'good' | 'work';
  icon?: React.ReactNode;
  /** A line under a topic: the coach's tip, or a plain hint. */
  adviceFor?: (topic: TopicFact) => string;
}

/** One column of the review: topics of this attempt with their score, and how mastery moved. */
export function TopicList({ testId, title, empty, topics, tone, icon, adviceFor }: TopicListProps) {
  const { t } = useTranslation();

  return (
    <div data-testid={testId} className="min-w-0">
      <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
      {topics.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="mt-2">
          {topics.map((topic) => (
            <li key={`${topic.subject}:${topic.topic}`} className="border-t border-border py-3 first:border-t-0">
              <div className="flex items-center justify-between gap-3">
                <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
                  {icon && <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground text-background">{icon}</span>}
                  <span className="truncate">{topic.topic}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2 text-xs tabular-nums text-muted-foreground">
                  {t('studyPlan.review_topic_score', { correct: topic.correct, total: topic.total })}
                  {topic.delta !== null && topic.delta !== 0 && (
                    <span className={cn('rounded-md px-1.5 py-0.5 font-semibold', topic.delta > 0 ? 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400' : 'bg-error-50 text-error-700 dark:bg-error-900/30 dark:text-error-400')}>
                      {t(topic.delta > 0 ? 'studyPlan.review_delta_up' : 'studyPlan.review_delta_down', { points: Math.abs(topic.delta) })}
                    </span>
                  )}
                </span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-muted" role="img" aria-label={`${topic.topic}: ${topic.percent}%`}>
                <div className={cn('h-full rounded-full', tone === 'good' ? 'bg-foreground' : 'bg-error-600')} style={{ width: `${topic.percent}%` }} />
              </div>
              {adviceFor && <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{adviceFor(topic)}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
